import {BillingFormOverrides, FieldPropertiesByTarget, FormFieldTarget, StepId} from '../../types/checkoutApp.types';
import {PaymentMethod as cashierPaymentMethod} from '@wix/cashier-payments-widget';
import {PaymentsWidgetBIData} from '@wix/cashier-payments-widget/dist/src/types/BI';
import {getUniques} from './common/bi.util.common';
import {CartModel} from '../models/checkout/Cart/Cart.model';
import {DeliveryMethodModel} from '../models/checkout/Cart/DeliveryMethod.model';
import {FormOverride} from '@wix/form-viewer/dist/types/types';
import {isCheckoutEligibleReduceFields} from './isCheckoutEligibleReduceFields.utils';

export enum FlowType {
  fastFlow = 'fast flow',
  fullFlow = 'full flow',
  leanFlow = 'lean flow',
}

export enum DeliveryMethodType {
  pickup = 'pickup',
  nonPickup = 'non-pickup',
  shipping = 'shipping',
  none = 'none',
}

export enum StepName {
  PLACE_ORDER_FAST_FLOW = 'PlaceOrderFastFlow',
  SHIPPING_DETAILS = 'ShippingDetails',
  ONLOAD_OR_UPDATE_FAST_FLOW = 'OnLoadOrUpdateFastFlow',
}

export enum BiAddressActionOrigin {
  UserIntent = 'user-intent',
  AddressError = 'address-error',
}

export enum BiFoldableSummaryAction {
  open = 'open',
  close = 'close',
}

export enum BiErrorDialogAction {
  clickOnCta = 'clickOnCta',
  clickOnX = 'clickOnX',
}

export enum BiMobilePosition {
  top = 'Top',
  bottom = 'Bottom',
}

export enum BiMemberAction {
  login = 'login',
  logout = 'logout',
  signup = 'signup',
}

export enum CheckoutLoadedWithErrorReason {
  HasPremiumError = 'hasPremiumError',
  CannotShip = 'cannotShip',
}

export const ECOM_PLATFORM_CHECKOUT = 'ecom-platform-checkout';

export const getCatalogAppIds = (cart: CartModel): string => {
  return getMappedUniqueList(cart.lineItems, ({source}) => source.catalogReference?.appId);
};

export const getPaymentsWidgetBIData = (cart: CartModel): PaymentsWidgetBIData => {
  return {
    purchaseFlowId: cart.purchaseFlowId,
    catalogAppId: getCatalogAppIds(cart),
  };
};

export const getCustomFieldsTitles = (cart: CartModel): string | undefined => {
  return cart.customField?.title;
};

export const isCustomFieldMandatory = (formFieldsProperties: FieldPropertiesByTarget): boolean => {
  return Boolean(formFieldsProperties?.[FormFieldTarget.additional_info_1]?.required);
};

export const getItemTypes = (cart: CartModel): string => {
  return getMappedUniqueList(cart.lineItems, ({attributes}) =>
    attributes.itemType?.preset ? attributes.itemType?.preset.toString() : attributes.itemType?.custom
  );
};

function getMappedUniqueList<T>(arr: T[] | undefined, mapper: (t: T) => string | undefined): string {
  return getUniques((arr ?? /* istanbul ignore next */ []).map(mapper).filter(isDefined) as string[]).toString();
}

export const getPaymentCategory = (paymentMethod?: string) => {
  switch (paymentMethod) {
    case cashierPaymentMethod.Offline:
      return 'Unknown';
    // istanbul ignore next
    case cashierPaymentMethod.CreditCard:
      return 'CreditCard';
    // istanbul ignore next
    default:
      return 'Ewallet';
  }
};

const isDefined = <T>(obj: T): boolean => {
  return !!obj;
};

export const getFlowType = (isFastFlow: boolean, isLeanFlow: boolean): FlowType => {
  if (isFastFlow) {
    return FlowType.fastFlow;
  } else if (isLeanFlow) {
    return FlowType.leanFlow;
  }

  return FlowType.fullFlow;
};

export const getFirstStage = (isFastFlow: boolean, firstStage: StepId): StepId => {
  if (isFastFlow) {
    return StepId.paymentAndPlaceOrder;
  } else {
    return firstStage;
  }
};

export const getSelectedDeliveryMethodType = (selectedDeliveryMethod?: DeliveryMethodModel): DeliveryMethodType => {
  let deliveryMethodType = DeliveryMethodType.none;

  if (selectedDeliveryMethod) {
    deliveryMethodType = selectedDeliveryMethod.pickup
      ? DeliveryMethodType.pickup
      : /* istanbul ignore next */ DeliveryMethodType.shipping;
  }

  return deliveryMethodType;
};

export const hasSavedAddress = (cart: CartModel): boolean => {
  return Boolean(cart.paymentInfo.addressId || cart.deliveryInfo?.addressId);
};

export function getHiddenOverridesData({
  overrides,
  showRequiredBillingFields,
  isMember,
  isTaxConfigured,
  hasItemWithBillingTaxAddress,
}: {
  overrides: BillingFormOverrides;
  showRequiredBillingFields: boolean;
  isMember?: boolean;
  isTaxConfigured: boolean;
  hasItemWithBillingTaxAddress?: boolean;
}) {
  const checkoutEligibleReduceFields = isCheckoutEligibleReduceFields({
    showRequiredBillingFields,
    isMember,
    isTaxConfigured,
    hasItemWithBillingTaxAddress,
  });

  const hiddenFields = checkoutEligibleReduceFields
    ? Object.entries({
        fullName: (overrides[FormFieldTarget.full_name] as FormOverride)?.hidden,
        phone: overrides[FormFieldTarget.phone]?.hidden,
        companyName: !overrides[FormFieldTarget.company_name] || overrides[FormFieldTarget.company_name]?.hidden,
        addressLine: overrides[FormFieldTarget.address]?.addressLine?.hidden,
        city: overrides[FormFieldTarget.address]?.city?.hidden,
        postalCode: overrides[FormFieldTarget.address]?.postalCode?.hidden,
        vatId: !overrides[FormFieldTarget.vat_id],
      })
        .filter(([, value]) => value)
        .map(([key]) => key)
    : [];

  return {
    checkoutEligibleReduceFields,
    numOfHiddenFields: hiddenFields.length,
    hiddenFields: hiddenFields.join(','),
  };
}
